import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { GroupTagType } from '@tyro/api';
import { getBaseColorBasedOnString } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DraggableCard } from './draggable-card';
import { EmptyGroupPlaceholder } from './empty-group-placeholder';
import type { ListManagerState } from './state/types';
import { TeacherCards } from './teacher-cards';

const getListStyle = (isDraggingOver: boolean) =>
  ({
    backgroundColor: isDraggingOver ? 'indigo.200' : 'slate.200',
    borderColor: isDraggingOver ? 'indigo.100' : 'slate.100',
    borderRadius: 2,
    paddingY: 0.75,
    paddingX: 1.5,
  }) as const;

interface GroupColumnProps {
  group: ListManagerState;
}

export function GroupColumn({ group }: GroupColumnProps) {
  const { t } = useTranslation(['common']);
  const { spacing } = useTheme();
  const showEmptyGroupPlaceholder = group?.students?.length === 0;

  const programmeStage = useMemo(
    () =>
      group.groupTags?.filter(
        (tag) => tag.type === GroupTagType.ProgrammeStage,
      ),
    [group.groupTags],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      getData: () => ({
        type: 'group',
        droppableId: group.id,
      }),
      onDragEnter() {
        setIsDraggingOver(true);
      },
      onDragLeave() {
        setIsDraggingOver(false);
      },
      onDrop() {
        setIsDraggingOver(false);
      },
    });
  }, [group.id]);

  return (
    <Box sx={getListStyle(isDraggingOver)}>
      <Box sx={{ width: spacing(22) }}>
        <Stack sx={{ p: 1.5 }}>
          <Typography
            component="h2"
            variant="h6"
            sx={{ fontSize: '0.875rem !important' }}
          >
            {group.name}
          </Typography>
          {Array.isArray(programmeStage) && programmeStage.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                my: 0.5,
                mx: -0.5,
              }}
            >
              {programmeStage.map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  color={getBaseColorBasedOnString(tag.name)}
                  variant="soft"
                  size="small"
                  sx={{
                    '&': {
                      borderRadius: 0.75,
                    },
                    '& .MuiChip-label': {
                      fontSize: 10,
                    },
                  }}
                />
              ))}
            </Box>
          )}
          <Typography component="span" variant="caption">
            {t('common:numOfMembers', {
              count: group?.students?.length ?? 0,
            })}
          </Typography>
        </Stack>
        <TeacherCards teachers={group?.staff} isDraggingOver={isDraggingOver} />
        <Box ref={containerRef}>
          {group?.students?.map((student, index) => (
            <DraggableCard
              key={student.id}
              index={index}
              student={student}
              groupId={group.id}
            />
          ))}
          {showEmptyGroupPlaceholder && (
            <EmptyGroupPlaceholder
              sx={{
                borderColor: isDraggingOver ? 'indigo.500' : 'slate.400',
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
